import { Colors } from '@hawkins/variables';
import { Coordinates, FramingChartText } from '../types';
import { Resolution } from 'src/domains/calculators/utilities/types';
import { SIEMENS_STAR_SEGMENTS, TEXT } from './constants';

export function drawChartText(
  ctx: CanvasRenderingContext2D,
  text: FramingChartText,
  textCoordinates: Coordinates,
  textScale: number
): void {
  const { width: framingDecisionWidth, height: framingDecisionHeight } =
    text.framingDecision;

  const { aspectRatioText, cameraName, dop, sensorModeName, showTitle } = text;

  const { x } = textCoordinates;
  let y = textCoordinates.y;

  const largeResolution = framingDecisionHeight * TEXT.LARGE_SIZE_RATIO;
  const mediumResolution = framingDecisionHeight * TEXT.MEDIUM_SIZE_RATIO;
  const lineHeight = TEXT.LINE_HEIGHT;

  ctx.textAlign = 'center';
  ctx.fillStyle = Colors.Black;

  // Save the current canvas state
  ctx.save();

  // Apply the horizontal scaling based on the squeeze factor
  ctx.transform(textScale, 0, 0, 1, 0, 0);

  // Camera name (large)
  ctx.font = `bold ${largeResolution}px Arial`;
  ctx.fillText(cameraName, x, y);
  y += largeResolution;

  // Smaller Chart Text Elements
  ctx.font = `${mediumResolution}px Arial`;

  const textItems = [
    `Show: ${showTitle}`,
    `DoP: ${dop}`,
    `Sensor Mode: ${sensorModeName}`,
    `Framing Decision: ${framingDecisionWidth} x ${framingDecisionHeight}`,
    `Aspect Ratio: ${aspectRatioText}`,
  ];

  textItems.forEach((item) => {
    ctx.fillText(item, x, y);
    y += mediumResolution * lineHeight;
  });

  // Restore the canvas state
  ctx.restore();
}

/**
 * Draws test patterns (Siemens stars) at the corners
 */
export function drawTestPatterns(
  ctx: CanvasRenderingContext2D,
  testPatternResolution: Resolution,
  testPatternCoordinates: Coordinates[]
): void {
  const { width, height } = testPatternResolution;

  testPatternCoordinates.forEach(({ x, y }) => {
    drawSiemensStar(ctx, x, y, width, height);
  });
}

/**
 * Draws the Netflix logo on the canvas
 */
export function drawNetflixLogo(
  ctx: CanvasRenderingContext2D,
  logoImage: HTMLImageElement,
  logoResolution: Resolution,
  logoCoordinates: Coordinates
) {
  const { width, height } = logoResolution;
  const { x, y } = logoCoordinates;
  ctx.drawImage(logoImage, x, y, width, height);
}

export function drawCenterCross(
  ctx: CanvasRenderingContext2D,
  centerCrossResolution: Resolution,
  centerCrossCoordinates: Coordinates
): void {
  const { x, y } = centerCrossCoordinates;
  const { width, height } = centerCrossResolution;

  ctx.lineWidth = 7;
  ctx.strokeStyle = Colors.Red700;

  // Horizontal line
  ctx.beginPath();
  ctx.moveTo(x - width / 2, y);
  ctx.lineTo(x + width / 2, y);
  ctx.stroke();

  // Vertical line
  ctx.beginPath();
  ctx.moveTo(x, y - height / 2);
  ctx.lineTo(x, y + height / 2);
  ctx.stroke();
}

function drawSiemensStar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  const angleStep = Math.PI / SIEMENS_STAR_SEGMENTS;

  ctx.fillStyle = '#000000'; // Black

  for (let i = 0; i < SIEMENS_STAR_SEGMENTS; i++) {
    const startAngle = i * 2 * angleStep;
    const endAngle = startAngle + angleStep;

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.ellipse(x, y, width / 2, height / 2, 0, startAngle, endAngle);
    ctx.closePath();
    ctx.fill();
  }
}
